import type { FilterSchema } from '@/shared/components/filter/validation'
import type { TFunction } from 'i18next'
import type { WidgetPeriods } from '../types'

import React, { useMemo, useState } from 'react'
import { addDays, format } from 'date-fns'
import { any, prop } from 'lodash/fp'
import { ArrowRightLeftIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useBIAnalyticQuery, useBIRevisionQuery } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'

import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/ui/select'
import { ampli } from '@/shared/lib/ampli'
import { DateFormat } from '@/shared/tools/dates'
import { formatMoney, NumberFormats } from '@/shared/tools/locale'

import PercentDifferenceBadge from '../../PercentDifferenceBadge'
import { Widget, WidgetContent, WidgetHeader } from '../../Widget'
import WidgetErrorState from '../../WidgetErrorState'
import WidgetLoadingState from '../../WidgetLoadingState'

const getCurrentPeriodTypeOptions = (t: TFunction) => [
	{
		label: t('current_week'),
		value: 'week_key',
	},
	{
		label: t('current_day'),
		value: 'date_key',
	},
]
const getComparedPeriodTypeOptions = (t: TFunction) => [
	{
		label: t('prev_week'),
		value: 'week_key',
	},
	{
		label: t('prev_day'),
		value: 'date_key',
	},
]
function getWeeklyPeriods(integrationDate: string) {
	const integration = new Date(integrationDate)

	return {
		main: {
			start_date: format(addDays(integration, -7), DateFormat.system),
			end_date: format(integration, DateFormat.system),
		},
		compared: {
			start_date: format(addDays(integration, -14), DateFormat.system),
			end_date: format(addDays(integration, -7), DateFormat.system),
		},
	}
}
function getDatePeriods(integrationDate: string) {
	const integration = new Date(integrationDate)

	return {
		main: {
			start_date: format(addDays(integration, -1), DateFormat.system),
			end_date: format(addDays(integration, -1), DateFormat.system),
		},
		compared: {
			start_date: format(addDays(integration, -2), DateFormat.system),
			end_date: format(addDays(integration, -2), DateFormat.system),
		},
	}
}
const periodTypeResolvers: Record<string, (d: string) => WidgetPeriods> = {
	week_key: getWeeklyPeriods,
	date_key: getDatePeriods,
}
const selectValue = prop('data[0][0]')
export default function MetricToCompareWidget({
	commonFilters,
}: {
	commonFilters: FilterSchema[]
}) {
	const { t } = useTranslation()

	const METRIC = 'revenue'
	const [periodType, setPeriodType] = useState<string>('week_key')
	const handlePeriodTypeChange = (value: string) => {
		ampli.dashboardBiSwitchRevenuePeriodInMetricToCompare({
			value,
		})
		setPeriodType(value)
	}
	const currency = useSelectedCurrency()
	const commonQueryParams = {
		currency,
		label: 'MetricToCompare',
		filters: commonFilters,
		start_date: null as unknown as string,
		end_date: null as unknown as string,
		metrics: [METRIC],
		dimensions: [],
		date_aggregation: null,
	}
	const biRevisionDateQuery = useBIRevisionQuery<WidgetPeriods>({
		select: (res) =>
			periodTypeResolvers[periodType](prop('date', res) as string),
	})
	const biQueryConfig = {
		select: selectValue,
		enabled: biRevisionDateQuery.isFetched,
	}
	const mainSummaryQuery = useBIAnalyticQuery<number>(
		{
			...commonQueryParams,
			...(biRevisionDateQuery.data?.main || {}),
		},
		biQueryConfig
	)
	const comparedSummaryQuery = useBIAnalyticQuery<number>(
		{
			...commonQueryParams,
			...(biRevisionDateQuery.data?.compared || {}),
		},
		biQueryConfig
	)
	const isLoading = any(prop('isLoading'), [
		mainSummaryQuery,
		comparedSummaryQuery,
		biRevisionDateQuery,
	])
	const isError = any(prop('isError'), [
		mainSummaryQuery,
		comparedSummaryQuery,
		biRevisionDateQuery,
	])
	const diffPercent =
		mainSummaryQuery.data && comparedSummaryQuery.data
			? (mainSummaryQuery.data * 100) / comparedSummaryQuery.data - 100
			: 0
	const currentPeriodTypeOptions = useMemo(
		() => getCurrentPeriodTypeOptions(t),
		[]
	)
	const comparedPeriodTypeOptions = useMemo(
		() => getComparedPeriodTypeOptions(t),
		[]
	)

	return (
		<Widget className='relative flex flex-auto flex-col'>
			<WidgetLoadingState isTinted={isLoading} />
			<WidgetErrorState isTinted={isError} />
			<WidgetHeader title={t('metric.revenue')} />
			<WidgetContent className='mt-5'>
				<div className='flex items-center gap-1'>
					<Select value={periodType} onValueChange={handlePeriodTypeChange}>
						<SelectTrigger className='w-full'>
							<SelectValue placeholder='Select period type' />
						</SelectTrigger>
						<SelectContent>
							<SelectGroup>
								{currentPeriodTypeOptions.map((option) => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectGroup>
						</SelectContent>
					</Select>
					<ArrowRightLeftIcon className='size-4 shrink-0' />
					<Select value={periodType} onValueChange={handlePeriodTypeChange}>
						<SelectTrigger className='w-full'>
							<SelectValue placeholder='Select period type' />
						</SelectTrigger>
						<SelectContent>
							<SelectGroup>
								{comparedPeriodTypeOptions.map((option) => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectGroup>
						</SelectContent>
					</Select>
				</div>
				<div className='mt-8 flex flex-col space-y-2'>
					<div className='flex items-center space-x-2'>
						<PercentDifferenceBadge value={diffPercent} />
						<span className='truncate text-xs text-muted-foreground'>
							({t('past')}:{' '}
							{comparedSummaryQuery.data != null &&
								formatMoney(
									comparedSummaryQuery.data,
									currency,
									NumberFormats.Integer
								)}
							)
						</span>
					</div>
					<div className='flex flex-col'>
						<span className='text-muted-foreground'>{t('current')}</span>
						<span className='data-text truncate'>
							{mainSummaryQuery.data != null &&
								formatMoney(
									mainSummaryQuery.data,
									currency,
									NumberFormats.Integer
								)}
						</span>
					</div>
				</div>
			</WidgetContent>
		</Widget>
	)
}
